import { useEffect, useState } from "react";

/**
 * Installing this site as an app (its own icon, full screen). Chrome and Edge offer it through
 * `beforeinstallprompt`, which fires once early on, so it's caught here at load and kept for the
 * button. iPads and iPhones have no prompt: the page explains Share › Add to Home Screen instead.
 */
type Prompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

let saved: Prompt | null = null;
const subs = new Set<() => void>();
const ping = () => subs.forEach((f) => f());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    saved = e as Prompt;
    ping();
  });
  window.addEventListener("appinstalled", () => {
    saved = null;
    ping();
  });
}

const standalone = () => window.matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
const apple = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export function useInstall() {
  const [, tick] = useState(0);
  useEffect(() => {
    const f = () => tick((n) => n + 1);
    subs.add(f);
    return () => void subs.delete(f);
  }, []);
  const installed = standalone();
  return {
    installed,
    /** The browser can install it with one tap. */
    canPrompt: !installed && !!saved,
    /** iPad / iPhone: installed by hand from the Share menu. */
    apple: !installed && apple(),
    install: async () => {
      if (!saved) return false;
      await saved.prompt();
      const { outcome } = await saved.userChoice;
      saved = null;
      ping();
      return outcome === "accepted";
    },
  };
}
